import { Option } from '@spike/model';
import {
    NewBookingAppointment,
    NewBookingOrder,
    RecurringSeriesDetail,
    RecurringSeriesEnd,
    RecurringSeriesRecurrence,
    RecurringSeriesRequest
} from '@spike/new-booking-model';
import { Moment } from 'moment-timezone';
import { AppointmentMultiple, DataVaraible } from 'model/NewMultipleBooking';
import {
    CONFLICT_PREFERENCE_LABELS,
    RECURRING_SERIES_MAX_HORIZON_YEARS,
    RecurringSeriesConfig
} from 'model/RecurringSeries';

export const MISSING_VALUE_PLACEHOLDER = '—';

export interface RecurringSeriesRuleLabels {
    frequency: string;
    end: string;
    conflictRule: string;
}

const titleCase = (id: string): string =>
    id
        .split('_')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const formatRecurringSeriesEnd = (end: RecurringSeriesEnd): string => {
    switch (end.type) {
        case 'never':
            return 'Never';
        case 'count':
            return `After ${end.count} Bookings`;
        case 'date':
            return `On ${end.date.format('MMM D, YYYY')}`;
        default: {
            const unhandled: never = end;
            return unhandled;
        }
    }
};

export const formatRecurringSeriesRule = (
    detail: RecurringSeriesDetail,
    frequencyOptions: Array<Option<string>>
): RecurringSeriesRuleLabels => {
    const frequencyId = detail.frequencyId;
    const frequency = frequencyId
        ? frequencyOptions.find(option => option.id === frequencyId)?.name ?? titleCase(frequencyId)
        : MISSING_VALUE_PLACEHOLDER;

    const end = detail.end ? formatRecurringSeriesEnd(detail.end) : MISSING_VALUE_PLACEHOLDER;

    const conflictRule = detail.conflictPreference
        ? CONFLICT_PREFERENCE_LABELS[detail.conflictPreference]
        : MISSING_VALUE_PLACEHOLDER;

    return { frequency, end, conflictRule };
};

export interface RecurringSeriesTemplate {
    appointments: Array<NewBookingAppointment>;
    order?: NewBookingOrder;
}

const getItemDate = (data: DataVaraible): Moment => data.boarding?.boarding?.from ?? data.date;

export const getSeriesAnchorDate = (multipleAppointments: Array<AppointmentMultiple>): Moment | undefined => {
    const dates = multipleAppointments
        .flatMap(appointment => appointment.variableData ?? [])
        .map(getItemDate)
        .filter((date): date is Moment => Boolean(date));

    if (dates.length === 0) {
        return undefined;
    }

    return dates.reduce((earliest, date) => (date.isBefore(earliest) ? date : earliest)).clone();
};

export const getMaxSeriesEndDate = (anchor: Moment): Moment =>
    anchor.clone().add(RECURRING_SERIES_MAX_HORIZON_YEARS, 'years');

export const isRecurringSeriesConfigValid = (config: RecurringSeriesConfig, anchor: Moment | undefined): boolean => {
    if (!config.frequency) {
        return false;
    }

    if (config.end.type === 'never') {
        return true;
    }

    if (config.end.type === 'after') {
        return Number.isInteger(config.end.bookings) && config.end.bookings > 0;
    }

    const endDate = config.end.date;

    if (!endDate) {
        return false;
    }

    if (!anchor) {
        return true;
    }

    return endDate.isAfter(anchor, 'day') && !endDate.isAfter(getMaxSeriesEndDate(anchor), 'day');
};

const toRecurrence = (config: RecurringSeriesConfig): RecurringSeriesRecurrence | undefined => {
    if (!config.frequency) {
        return undefined;
    }

    const base = { frequencyId: config.frequency.id, conflictPreference: config.conflicts };
    const end = config.end;

    switch (end.type) {
        case 'never':
            return { ...base, endType: 'never' };
        case 'after':
            return { ...base, endType: 'count', endCount: end.bookings };
        case 'on_date':
            return end.date ? { ...base, endType: 'date', endDate: end.date } : undefined;
        default: {
            const unhandled: never = end;
            return unhandled;
        }
    }
};

export const buildRecurringSeriesRequest = (
    template: RecurringSeriesTemplate | undefined,
    config: RecurringSeriesConfig,
    anchor: Moment | undefined
): RecurringSeriesRequest | undefined => {
    if (!template || template.appointments.length === 0 || !isRecurringSeriesConfigValid(config, anchor)) {
        return undefined;
    }

    const recurrence = toRecurrence(config);

    return recurrence && { appointments: template.appointments, order: template.order, recurrence };
};
